import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBook,
  faRotate,
  faChevronLeft,
  faChevronRight,
  faBug,
  faFeather,
  faLightbulb,
  faHandsHelping,
  faHeart,
  faCrown,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/Modal";

const GEN_USER_ID = "66f2af3fd885089a58f9ecb3";
const API_URL = process.env.REACT_APP_API_URL;
const fetchConfig = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const UserView = () => {
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [genCourses, setGenCourses] = useState([]);
  const [genCoursesLoading, setGenCoursesLoading] = useState(false);
  const [transferStatus, setTransferStatus] = useState(null);
  const [transferError, setTransferError] = useState(null);
  const [transferringCourseId, setTransferringCourseId] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, [id, page]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const url = `${API_URL}/support/user/${id}?page=${page}&limit=${limit}`;
      console.log("fetchUserData:", url);
      const response = await fetch(url, { ...fetchConfig, method: "GET" });
      const data = await response.json();
      setUserData(data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenCourses = async () => {
    setGenCoursesLoading(true);
    setGenCourses([]);
    try {
      const url = `${API_URL}/support/user/${GEN_USER_ID}?page=1&limit=100`;
      console.log("fetchGenCourses:", url);
      const res = await fetch(url, { ...fetchConfig, method: "GET" });
      const data = await res.json();
      setGenCourses(data.courses || []);
    } catch (err) {
      setGenCourses([]);
    } finally {
      setGenCoursesLoading(false);
    }
  };

  const handleOpenTransferModal = () => {
    setShowTransferModal(true);
    setTransferStatus(null);
    setTransferError(null);
    fetchGenCourses();
  };

  const handleTransferCourse = async (courseId) => {
    setTransferringCourseId(courseId);
    setTransferStatus(null);
    setTransferError(null);
    try {
      const url = `${API_URL}/support/transfer_course`;
      console.log("handleTransferCourse:", url, courseId);
      const res = await fetch(url, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify({ course_id: courseId, new_user_id: id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTransferStatus("success");
        setTimeout(() => {
          setShowTransferModal(false);
          fetchUserData();
        }, 1000);
      } else {
        setTransferError(data.error || "Transfer failed");
      }
    } catch (err) {
      setTransferError(err.message || "Transfer failed");
    } finally {
      setTransferringCourseId(null);
    }
  };

  const formatDate = (date) => {
    const messageDate = new Date(date);
    return messageDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#00d1b2";
      case "expired":
        return "#ff3860";
      default:
        return "#3273dc";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "bug":
        return faBug;
      case "feature":
        return faFeather;
      case "improvement":
        return faLightbulb;
      case "assistance":
        return faHandsHelping;
      case "appreciation":
        return faHeart;
      default:
        return faBug;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "bug":
      case "feature":
      case "improvement":
        return "#3273dc";
      case "assistance":
        return "#ffdd57";
      case "appreciation":
        return "#00d1b2";
      default:
        return "#3273dc";
    }
  };

  if (loading) {
    return (
      <div className="container mt-6 has-text-centered">
        <div className="neumorphic-inset p-6 is-inline-block">
          <div
            className="loader is-loading"
            style={{ width: 50, height: 50 }}
          ></div>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  const { user, courses, chats } = userData;

  return (
    <div className="container mt-4 mb-6">
      <div className="columns">
        {/* User Info Column */}
        <div className="column is-4">
          <div className="neumorphic p-5">
            <div className="has-text-centered mb-4">
              <div
                className="neumorphic-inset is-inline-flex is-justify-content-center is-align-items-center mb-4"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  fontSize: "2.5em",
                  color: "var(--accent-primary)",
                }}
              >
                {user.first_name[0].toUpperCase()}
              </div>
              <h1 className="title is-3 mb-2">{user.first_name}</h1>
              <p className="subtitle is-5 mb-2">{user.phone_number}</p>
              {user.premium_until && (
                <span
                  className="tag is-medium neumorphic"
                  style={{ color: "var(--accent-primary)" }}
                >
                  <span className="icon mr-1">
                    <FontAwesomeIcon icon={faCrown} />
                  </span>
                  Premium until {formatDate(user.premium_until)}
                </span>
              )}
            </div>

            {/* Add Subscription Info Section */}
            {user.subscription && (
              <div className="mt-4">
                <h3 className="title is-5 mb-3">Subscription Status</h3>
                {user.subscription.active_subscription ? (
                  <div className="neumorphic-inset p-3">
                    <p className="mb-2">
                      <strong>Active Plan:</strong>{" "}
                      {user.subscription.active_subscription.product_id}
                    </p>
                    <p className="mb-2">
                      <strong>Expires:</strong>{" "}
                      {formatDate(
                        user.subscription.active_subscription.expires_date
                      )}
                    </p>
                    <p>
                      <strong>Store:</strong>{" "}
                      {user.subscription.active_subscription.store}
                    </p>
                  </div>
                ) : (
                  <p className="has-text-grey">No active subscription</p>
                )}

                {user.subscription.subscription_history?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="title is-6 mb-3">Subscription History</h4>
                    <div className="neumorphic-inset p-3">
                      {user.subscription.subscription_history.map(
                        (sub, index) => (
                          <div
                            key={index}
                            className="mb-3 pb-3"
                            style={{
                              borderBottom:
                                index ===
                                user.subscription.subscription_history.length -
                                  1
                                  ? "none"
                                  : "1px solid var(--border-color)",
                            }}
                          >
                            <div className="is-flex is-justify-content-space-between align-items-center">
                              <span>{sub.product_id}</span>
                              <span
                                className="tag is-small"
                                style={{
                                  color: getSubscriptionStatusColor(sub.status),
                                }}
                              >
                                {sub.status}
                              </span>
                            </div>
                            <p className="is-size-7 mt-1">
                              {formatDate(sub.purchase_date)} -{" "}
                              {formatDate(sub.expires_date)}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="content mt-4">
              <p>
                <strong>Joined:</strong> {formatDate(user.created_at)}
              </p>
              <p>
                <strong>Courses Created:</strong> {courses.length}
              </p>
              <p>
                <strong>Total Interactions:</strong> {chats.length}
              </p>
              <button
                className="button is-neumorphic is-fullwidth mt-4"
                onClick={handleOpenTransferModal}
              >
                Transfer Course
              </button>
            </div>
          </div>

          {/* Recent Chats Section */}
          <div className="neumorphic p-5 mt-4">
            <h2 className="title is-4 mb-4">Recent Interactions</h2>
            {chats.map((chat) => (
              <Link
                to={`/chats/${chat._id}`}
                key={chat._id}
                className="chat-card-link"
              >
                <div className="neumorphic-inset p-3 mb-3">
                  <div className="is-flex is-justify-content-space-between mb-2">
                    <span className="tag is-small neumorphic">
                      {chat.type === "feedback" ? "Feedback" : "Review"}
                    </span>
                    <span
                      className="is-size-7"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {formatDate(chat.updated_at)}
                    </span>
                  </div>
                  <p className="mb-2" style={{ color: "var(--text-primary)" }}>
                    {chat.content.substring(0, 100)}...
                  </p>
                  <div className="is-flex is-justify-content-space-between">
                    {chat.category && chat.category !== "unknown" && (
                      <span
                        className="tag is-small"
                        style={{ color: getCategoryColor(chat.category) }}
                      >
                        <span className="icon">
                          <FontAwesomeIcon
                            icon={getCategoryIcon(chat.category)}
                          />
                        </span>
                        <span>{chat.category}</span>
                      </span>
                    )}
                    {chat.type === "course_review" && chat.rating && (
                      <span
                        className="tag is-small neumorphic"
                        style={{
                          color:
                            chat.rating >= 4
                              ? "#00d1b2"
                              : chat.rating >= 3
                              ? "#ffdd57"
                              : "#ff3860",
                        }}
                      >
                        {chat.rating}/5
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Courses Column */}
        <div className="column is-8">
          <div className="neumorphic p-5">
            <div className="is-flex is-justify-content-space-between align-items-center mb-4">
              <h2 className="title is-4 mb-0">Courses</h2>
              <div className="field has-addons">
                <p className="control">
                  <button
                    onClick={fetchUserData}
                    className="button is-neumorphic mr-2"
                    aria-label="Reload courses"
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faRotate} />
                    </span>
                  </button>
                </p>
                {page > 1 && (
                  <p className="control">
                    <button
                      onClick={() => setPage((prev) => prev - 1)}
                      className="button is-neumorphic"
                      aria-label="Previous page"
                    >
                      <span className="icon">
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </span>
                    </button>
                  </p>
                )}
                {courses.length === limit && (
                  <p className="control">
                    <button
                      onClick={() => setPage((prev) => prev + 1)}
                      className="button is-neumorphic"
                      aria-label="Next page"
                    >
                      <span className="icon">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </span>
                    </button>
                  </p>
                )}
              </div>
            </div>

            {courses.length === 0 ? (
              <div className="has-text-centered p-6">
                <p
                  className="is-size-5"
                  style={{ color: "var(--text-secondary)" }}
                >
                  No courses found
                </p>
              </div>
            ) : (
              <div className="courses-grid">
                {courses.map((course) => (
                  <Link to={`/course/${course._id}`} key={course._id}>
                    <div className="neumorphic p-4 mb-4">
                      <div className="is-flex is-justify-content-space-between align-items-center">
                        <div>
                          <h3 className="title is-5 mb-2">
                            <FontAwesomeIcon icon={faBook} className="mr-2" />
                            {course.title}
                          </h3>
                          <div className="tags">
                            <span className="tag is-small neumorphic">
                              {course.premium ? "Premium" : "Free"}
                            </span>
                            <span className="tag is-small neumorphic">
                              {course.chapters?.length || 0} chapters
                            </span>
                          </div>
                        </div>
                        <a
                          href={course.origin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="button is-small is-neumorphic"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="icon">
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </span>
                        </a>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Transfer Modal */}
      {showTransferModal && (
        <Modal onClose={() => setShowTransferModal(false)}>
          <div className="p-4">
            <h2 className="title is-4 mb-3">
              Transfer a Course to {user.first_name}
            </h2>
            {genCoursesLoading ? (
              <div className="has-text-centered p-4">
                <div
                  className="loader is-loading"
                  style={{ width: 30, height: 30 }}
                ></div>
              </div>
            ) : genCourses.length === 0 ? (
              <div className="has-text-centered">
                No courses found for GEN USER
              </div>
            ) : (
              <div className="courses-grid">
                {genCourses.map((course) => (
                  <div key={course._id} className="neumorphic p-4 mb-4">
                    <div className="is-flex is-justify-content-space-between align-items-center">
                      <div>
                        <h3 className="title is-5 mb-2">
                          <FontAwesomeIcon icon={faBook} className="mr-2" />
                          {course.title}
                        </h3>
                        <div className="tags">
                          <span className="tag is-small neumorphic">
                            {course.premium ? "Premium" : "Free"}
                          </span>
                          <span className="tag is-small neumorphic">
                            {course.chapters?.length || 0} chapters
                          </span>
                        </div>
                      </div>
                      <button
                        className={`button is-neumorphic ${
                          transferringCourseId === course._id
                            ? "is-loading"
                            : ""
                        }`}
                        onClick={() => handleTransferCourse(course._id)}
                        disabled={!!transferringCourseId}
                      >
                        Transfer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {transferStatus === "success" && (
              <div className="notification is-success mt-3">
                Transfer successful!
              </div>
            )}
            {transferError && (
              <div className="notification is-danger mt-3">{transferError}</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserView;
